/* Nutrient Meter Component */
import React, { useState } from "react";
import { useLang } from "../contexts/LangContext";
import { fishDatabase } from "../data/fish_db";
import { getFishNutrients } from "../data/fishWithNutrients";
import { convertInCurrentLang } from "../utils/misc";
import { NUTRIENT_REFERENCE_VALUES } from "../data/nutrientReferenceValues";

function NutrientBar({
  label,
  value,
  unit,
  max = 100,
}: {
  label: string;
  value: number;
  unit: string;
  max?: number;
}) {
  const percentage = Math.min((value / max) * 100, 100);
  const { t, lang } = useLang();
  const unit_map: Record<string, string> = {
    g: t("unit_g"),
    gm: t("unit_gm"),
    grams: t("unit_grams"),
    oz: t("unit_oz"),
    kcal: t("unit_kcal"),
    mg: t("unit_mg"),
    µg: t("unit_μg"),
    "µg RAE": t("unit_μg_rae"),
  };
  return (
    <div className="mb-1">
      <div className="flex justify-between text-sm">
        <span>{label}</span>
        <span>
          {convertInCurrentLang(lang, value.toFixed(1))}{" "}
          {unit_map[unit] || unit}
        </span>
      </div>
      <div className="w-full h-2 bg-slate-300 rounded">
        <div
          className="h-2 bg-blue-500 rounded"
          style={{ width: `${percentage}%` }}
        ></div>
      </div>
    </div>
  );
}

const NutrientMeter = ({
  fishes,
  showDRI = true,
}: {
  fishes: { fishId: string; amount_in_gm: number }[];
  showDRI?: boolean;
}) => {
  const { t, lang } = useLang();

  const nutrientKeyMap: Record<string, string> = {
    energy: t("energy"),
    protein: t("protein"),
    fat: t("fat"),    
    carbohydrates: t("carbohydrates"),
    omega3: t("omega3"),
    omega6: t("omega6"),
    calcium: t("calcium"),
    iron: t("iron"),
    zinc: t("zinc"),
    magnesium: t("magnesium"),
    phosphorus: t("phosphorus"),
    potassium: t("potassium"),
    sodium: t("sodium"),
    iodine: t("iodine"),
    copper: t("copper"),
    manganese: t("manganese"),
    selenium: t("selenium"),
    sulphur: t("sulphur"),
    vitaminA: t("vitaminA"),
    vitaminD: t("vitaminD"),
    vitaminE: t("vitaminE"),
    thiamin: t("thiamin"),
    riboflavin: t("riboflavin"),
    niacin: t("niacin"),
    vitaminB6: t("vitaminB6"),
    folate: t("folate"),
    vitaminB12: t("vitaminB12"),
  };

  const unit_map: Record<string, string> = {
    g: t("unit_g"),
    gm: t("unit_gm"),
    grams: t("unit_grams"),
    oz: t("unit_oz"),
    kcal: t("unit_kcal"),
    mg: t("unit_mg"),
    µg: t("unit_μg"),
    "µg RAE": t("unit_μg_rae"),
  };

  const defaultUnit: Record<string, string> = {
    energy: "kcal",
    protein: "g",
    fat: "g",    
    carbohydrates: "g",
    omega3: "mg",
    omega6: "mg",
    calcium: "mg",
    iron: "mg",
    zinc: "mg",
    magnesium: "mg",
    phosphorus: "mg",
    potassium: "mg",
    sodium: "mg",
    iodine: "µg",
    copper: "mg",
    manganese: "mg",
    selenium: "µg",
    sulphur: "mg",
    vitaminA: "µg RAE",
    vitaminD: "µg",
    vitaminE: "mg",
    thiamin: "mg",
    riboflavin: "mg",
    niacin: "mg",
    vitaminB6: "mg",
    folate: "µg",
    vitaminB12: "µg",
  };

  // Use shared reference values
  const DRI = NUTRIENT_REFERENCE_VALUES;

  type UnitKey = "g" | "mg" | "µg" | "µg RAE" | "kcal" | "kj";

  const convertUnit = (
    from: UnitKey,
    to: UnitKey,
    value: number
  ): number | null => {
    // Special case: energy conversion from kj to kcal
    if (from === "kj" && to === "kcal") {
      return value / 4.184; // 1 kcal = 4.184 kJ
    }
    
    // For other units, use base conversion
    const unitMap: Record<UnitKey, number> = {
      g: 1,
      mg: 1e-3,
      µg: 1e-6,
      "µg RAE": 1e-6, // Treated same as µg for comparison
      kcal: 1, // No conversion needed
      kj: 1, // Will be handled by special case above
    };

    if (!(from in unitMap) || !(to in unitMap)) return null;

    const valueInGrams = value * unitMap[from];
    const convertedValue = valueInGrams / unitMap[to];

    return convertedValue;
  };

  const totalNutrients: Record<string, { value: number; unit: string }> = {};
  fishes.forEach(({ fishId, amount_in_gm }) => {
    const species = fishDatabase.find((f) => f.id === fishId);
    // console.log("Processing fish:", fishId, species?.english_name, "Amount:", amount_in_gm);
    if (!species) return;
    
    // Get nutrients from CSV with fallback to embedded data
    const nutrients = getFishNutrients(fishId);
    if (!nutrients) return;
    
    Object.entries(nutrients).forEach(
      ([key, { value, unit }]) => {
        const defaultUnitValue = defaultUnit[key] || unit;
        const convertedValue = convertUnit(
          unit as UnitKey,
          defaultUnitValue as UnitKey,
          value
        );
        if (convertedValue !== null) {
          if (!totalNutrients[key]) {
            totalNutrients[key] = { value: 0, unit: defaultUnitValue };
          }
          totalNutrients[key].value += (convertedValue * amount_in_gm) / 100;
        }
      }
    );
  });

  // console.log("Total Nutrients:", totalNutrients);

  const scaledNutrients = Object.entries(totalNutrients).map(
    ([key, { value, unit }]) => ({
      key,
      label: nutrientKeyMap[key] || key,
      value: value,
      unit: unit,
    })
  );

  // Define macronutrients to show as bars
  const macroNutrients = ["energy", "protein", "fat", "omega3"];
  
  // Filter micronutrients and hide those with very small values (< 1% of DRI or < 0.1 in absolute value)
  const microNutrients = scaledNutrients
    .filter((n) => !macroNutrients.includes(n.key))
    .filter((n) => {
      // Hide if value is very small
      if (n.value < 0.1) return false;
      
      // Hide if percentage of DRI is very low (< 1%)
      const dri = DRI[n.key];
      if (dri && (n.value / dri) * 100 < 1) return false;
      
      return true;
    });
  
  // State for showing/hiding additional micronutrients
  const [showAllMicro, setShowAllMicro] = useState(false);
  const visibleMicroCount = 6;
  const hiddenMicroCount = microNutrients.length - visibleMicroCount;

  return (
    <div className="py-2">
      {/* Macronutrients (e.g. energy, protein, fat, omega-3) */}
      <div className="mb-3">
        {macroNutrients
          .map((key) => scaledNutrients.find((n) => n.key === key))
          .filter((n): n is { key: string; label: string; value: number; unit: string } => n !== undefined)
          .map((n) =>
            showDRI ? (
              <NutrientBar
                key={n.key}
                label={n.label}
                value={n.value}
                unit={n.unit}
                max={DRI[n.key] || 100}
              />
            ) : (
              <div
                key={n.key}
                className="flex justify-between items-center text-sm mb-1 px-3 py-1.5 bg-white rounded-lg border border-gray-200"
              >
                <span className="font-medium text-gray-700">{n.label}</span>
                <span className="text-blue-700 font-semibold">
                  {convertInCurrentLang(lang, n.value.toFixed(1))}{" "}
                  <span className="text-xs text-gray-500">{unit_map[n.unit] || n.unit}</span>
                </span>
              </div>
            )
          )}
      </div>

      {/* Micronutrients */}
      {microNutrients.length > 0 && (
        <div className="flex flex-wrap gap-1 text-sm text-gray-700">
          {microNutrients
            .slice(0, showAllMicro ? undefined : visibleMicroCount)
            .map((n) => (
              <span
                key={n.key}
                className="bg-gray-50 px-2 py-1 rounded-lg border text-xs"
              >
                {n.label}: {convertInCurrentLang(lang, n.value.toFixed(1))}{" "}
                {unit_map[n.unit] || n.unit}
              </span>
            ))}
          
          {/* Show more/less button */}
          {hiddenMicroCount > 0 && (
            <button
              onClick={() => setShowAllMicro(!showAllMicro)}
              className="bg-blue-100 hover:bg-blue-200 px-2 py-1 rounded-lg border border-blue-300 text-xs text-blue-700 font-medium transition-colors"
            >
              {showAllMicro
                ? t("showLessNutrients")
                : t("showMoreNutrients").replace("{count}", convertInCurrentLang(lang, String(hiddenMicroCount)))}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default NutrientMeter;
